import * as React from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Card,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material"
import dayjs from "dayjs"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  fetchDiscountById,
  updateDiscount,
  attachProductsToDiscount,
  detachProductsFromDiscount,
} from "@/api/discountService"
import { useCustomForm } from "@/hooks/useCustomForm"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import ProductSelector from "@/pages/discount/ProductSelector"
import { formatToDevanagariStyle } from "@/utilities/helper"
import FixedAmountProductList from "@/components/Discount/FixedAmountProductList"
import DiscountProductManager from "@/components/Discount/DiscountProductManager"
import { DiscountImport } from "@/components/Discount/DiscountImport"

const discountSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["percentage", "fixed", "fixed_price"]),
  value: z.coerce.number().min(0, "Value must be greater than 0"),
  is_active: z.boolean(),
  starts_at: z.string().nullable(),
  ends_at: z.string().nullable(),
})

export default function DiscountDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: discount, isLoading } = useQuery({
    queryKey: ["discount", id],
    queryFn: () => fetchDiscountById(id),
  })

  const discountData = discount?.data

  const { handleSubmit, control, Controller, reset, watch } = useCustomForm({
    resolver: zodResolver(discountSchema),
  })

  const finalPrice = (product) => {
    const price = Number(product.sell_price)
    const value = Number(discountData.value || 0)

    if (discountData.type === "percentage") {
      return Math.max(0, price - (value / 100) * price)
    }

    if (discountData.type === "fixed") {
      return Math.max(0, price - value)
    }

    if (discountData.type === "fixed_price") {
      const override = Number(product.pivot?.override_value)

      if (!override) return price

      return Math.max(0, override)
    }

    return price
  }

  React.useEffect(() => {
    if (discount?.data) {
      reset({
        name: discountData.name,
        type: discountData.type,
        value: discountData.value,
        is_active: discountData.is_active,
        starts_at: discountData.starts_at
          ? dayjs(discountData.starts_at).format("YYYY-MM-DD")
          : "",
        ends_at: discountData.ends_at
          ? dayjs(discountData.ends_at).format("YYYY-MM-DD")
          : "",
      })
    }
  }, [discount, reset])

  const discountType = watch("type")
  if (isLoading) return <div>Loading...</div>

  return (
    <>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
      >
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Discount Name"
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Type"
                    fullWidth
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="fixed_price">Fixed Price</option>
                  </TextField>
                )}
              />
            </Grid>

            {discountType !== "fixed_price" && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="value"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Value"
                      onFocus={(event) => {
                        event.target.select()
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="starts_at"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Starts At"
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="ends_at"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ends At"
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Active"
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Card>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
      >
        {discountData && discountData.type == "fixed_price" ? (
          <FixedAmountProductList />
        ): (   <DiscountImport />)}

        <DiscountProductManager
          discount={discountData}
          finalPrice={finalPrice}
        />
      </Card>
    </>
  )
}
